import { observer } from 'mobx-react-lite';
import styled from 'styled-components';
import { useTranslations } from 'next-intl';
import Link from 'next/link';
import { useRouter } from 'next/router';

import { useStore } from '@/stores/store-provider';
import { formatForCurrency } from '@/utils';
import Button from '@/components/button';
import IncrementDecrement from '@/components/increment-decrement';
import { media } from '@/themes';
import config from '@/config';

const Content = styled.div`
  display: flex;
  justify-content: flex-end;
  max-width: ${({ theme }) => theme.maxContentWidth};
  left: 50%;
  padding: 0 1em;
  position: fixed;
  top: 4em;
  transform: translateX(-50%);
  width: 100%;
  z-index: 2000;
`;

const Container = styled.div`
  background: ${props => props.theme.colors.background.card};
  border-radius: .25em;
  box-shadow: ${({ theme }) => theme.shadows.primary};
  color: ${props => props.theme.colors.typography.primary};
  flex-direction: column;
  display: flex;
  margin-top: .75em;
  outline: none;
  transform-origin: right top;
  width: 22em;
  z-index: 5;

  ${media.sm`
    border-radius: 0;
    margin-top: 0;
    width: 100%;
  `};

  &::before {
    border-style: solid;
    border-width: 0 .5em .5em .5em;
    border-color: transparent transparent white transparent;
    bottom: calc(100% - .75em);
    content: '';
    height: 0;
    position: absolute;
    right: 2em;
    width: 0;

    ${media.sm`
      display: none;
    `};
  }
`;

const InnerContainer = styled.div`
  padding: 1em;
`;

const CartOverlay = styled.div`
  background: rgba(0, 0, 0, .5);
  bottom: 0;
  left: 0;
  position: fixed;
  right:0;
  top: 0;
  z-index: 10;
`;

const CardsList = styled.div`
  margin: -.75em 0;

  ${media.sm`
    max-height: calc(100vh - 18.25em);
    overflow-y: auto;
  `};
`;

const BrandRow = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
  margin: .75em 0;
`;

const BrandDetails = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  font-size: 14px;
`;

const BrandLogoContainer = styled.div`
  background: ${props => props.background || '#ffffff'};
  border-radius: 5px;
  border: 1px solid #eeeeee;
  display: flex;
  justify-content: center;
  align-items: center;
  margin-right: 10px;
`;

const BrandLogo = styled.img`
  width: 54px;
  margin: 5px 3px;
`;

const BrandName = styled.div`
  text-overflow: ellipsis;
  white-space: nowrap;
  overflow: hidden;
  width: 160px;
  font-weight: 600;
`;

const CartActions = styled.div`
  align-items: center;
  background: #FAFAFA;
  border-top: 1px solid rgba(0, 0, 0, .05);
  border-radius: 0 0 .25em .25em;
  display: flex;
  justify-content: flex-end;
  padding: 1em;

  .button + .button {
    margin-left: 1em;
  }
`;

const EmptyStateContainer = styled.div`
  align-items: center;
  display: flex;
  flex-direction: column;
  padding: 1em 0;
`;

const EmptyStateLabel = styled.p`
  font-size: 1em;
  margin: 0;
`;

const ImportantInfo = styled(InnerContainer)`
  border: 1px solid rgba(0, 0, 0, .05);
  border-left: .25em solid ${props => props.theme.colors.status.warning};
  border-radius: .25em;
  margin: 1em;
  text-transform: none;
  white-space: normal;

  ul {
    font-size: .85em;
    margin: 0;
    padding: 0;
    list-style: none;

    li + li {
      margin-top: 1em;
    }
  }
`;

const ImportantTitle = styled.h4`
  color: ${props => props.theme.colors.status.warning};
  font-weight: bold;
  margin: 0 0 .5em;
`;

const Cart = observer(({ inline = true }) => {
  const store = useStore();
  const router = useRouter();
  const t = useTranslations('cart');
  const {
    items,
    toggleCart,
    creatingCharge,
    incrementQuantity,
    decrementQuantity,
    visible
  } = store;

  const handleCheckoutClick = () => {
    toggleCart();

    if (router.pathname === '/dapp') {
      return store.initDappCheckout();
    }

    return router.push('/checkout');
  };

  if (!store.visible) return null;

  return (
    <>
      <Content>
        <Container tabIndex={1} className={`cart ${inline ? 'cart--inline' : 'cart--popover'}`}>
          {items.length > 0 &&
            <InnerContainer>
              <CardsList>
                {items.map(cartItem => (
                  <BrandRow key={`${cartItem.brand.id}-${cartItem.amount}`}>
                    <BrandDetails>
                      <BrandLogoContainer background={cartItem.brand.backgroundColor}>
                        <BrandLogo src={cartItem.brand.imageUrl} />
                      </BrandLogoContainer>
                      <div>
                        <BrandName>{cartItem.brand.name}</BrandName>
                        <span className="amount">{formatForCurrency({ amount: cartItem.amount, currency: cartItem.product.currency })}</span>
                      </div>
                    </BrandDetails>
                    <div className="cart__item__actions">
                      {inline &&
                        <IncrementDecrement
                          small
                          onIncrement={() => incrementQuantity(cartItem)}
                          onDecrement={() => decrementQuantity(cartItem)}
                          value={cartItem.quantity} />
                      }
                    </div>
                  </BrandRow>
                ))}
              </CardsList>
            </InnerContainer>
          }
          {!items.length &&
            <>
              <EmptyStateContainer>
                <EmptyStateLabel>{t('empty')}</EmptyStateLabel>
              </EmptyStateContainer>
              <CartActions>
                <Button
                  primary
                  onClick={toggleCart}
                  size="small">
                  {t('keepShopping')}
                </Button>
              </CartActions>
            </>
          }
          {items.length > 0 &&
            <>
              <ImportantInfo>
                <ImportantTitle>{t('importantTitle')}</ImportantTitle>
                <ul>
                  <li>{t('exchangeWarning')}</li>
                  <li>{t('salesAreFinal')}</li>
                </ul>
              </ImportantInfo>
              {!creatingCharge &&
                <CartActions>
                  {inline &&
                    <Button
                      ghost
                      onClick={toggleCart}
                      size="small">
                      {t('keepShopping')}
                    </Button>
                  }
                  {!config.inMaintenanceMode &&
                    <Button primary size="small" onClick={handleCheckoutClick}>
                      {t('checkout')}
                    </Button>
                  }
                </CartActions>
              }
              {creatingCharge && <div className="loading">{t('loading')}</div>}
            </>
          }
        </Container>
      </Content>
      {visible && <CartOverlay onClick={toggleCart} />}
    </>
  );
});

export default Cart;
